/* eslint-disable react/no-unescaped-entities */
/**
 * Fieldlink help-centre articles.
 *
 * Hard-coded, curated, NZ-civil-tradie voice. Each article is plain JSX so we
 * can sprinkle in <Link>s, screenshots later, and inline highlighting.
 * Roles: every article in the office help-centre assumes the reader is an
 * admin or office user.
 *
 * Article shape:
 *   { id, title, blurb, body: (React element), tags: string[] }
 */
import { Link } from "react-router-dom";

const Note = ({ children }) => (
  <div className="border-l-4 border-[#FF5F15] bg-orange-50 dark:bg-[#FF5F15]/10 p-3 my-3 text-sm">
    <span className="font-bold text-[#FF5F15] uppercase tracking-wider text-[10px] mr-1.5">Heads up</span>
    {children}
  </div>
);

const Tip = ({ children }) => (
  <div className="border-l-4 border-green-600 bg-green-50 dark:bg-green-900/10 p-3 my-3 text-sm">
    <span className="font-bold text-green-700 uppercase tracking-wider text-[10px] mr-1.5">Pro tip</span>
    {children}
  </div>
);

const Steps = ({ children }) => (
  <ol className="list-decimal pl-5 space-y-2 text-sm my-3 marker:font-bold marker:text-[#FF5F15]">{children}</ol>
);

const P = ({ children }) => <p className="text-sm leading-relaxed my-3">{children}</p>;
const K = ({ children }) => (
  <code className="bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 px-1 py-0.5 text-[12px] font-mono rounded-sm">
    {children}
  </code>
);

/** ---------------- CATEGORIES ---------------- **/
export const HELP_CATEGORIES = [
  { id: "start", label: "Getting started", emoji: "🚀" },
  { id: "team", label: "Team & roles", emoji: "👷" },
  { id: "timesheets", label: "Timesheets & payroll", emoji: "🕒" },
  { id: "jobs", label: "Jobs & dispatch", emoji: "🛠️" },
  { id: "safety", label: "Safety & compliance", emoji: "🦺" },
  { id: "fleet", label: "Fleet & RUC", emoji: "🚛" },
  { id: "billing", label: "Quotes & invoices", emoji: "💸" },
  { id: "admin", label: "Account & billing", emoji: "⚙️" },
];

/** ---------------- ARTICLES ---------------- **/
export const HELP_ARTICLES = [
  /* ---- GETTING STARTED ---- */
  {
    id: "welcome",
    category: "start",
    title: "Welcome to Fieldlink — read this first",
    blurb: "A 60-second tour of what's where, and the order I'd set things up in.",
    tags: ["onboarding", "tour"],
    body: (
      <>
        <P>
          Fieldlink is built around three day-to-day jobs: <b>get hours off the lads</b>,
          <b> keep the site compliant</b>, and <b>get paid by the client</b>. Everything
          in the sidebar feeds one of those three.
        </P>
        <P><b>The order I'd set things up in:</b></P>
        <Steps>
          <li><b>Branding</b> → upload your logo so every PDF goes out with your name on it.</li>
          <li><b>Payroll</b> → set your pay-week end day (NZ default is Saturday). Every worker's "this week" tile then matches your pay run.</li>
          <li><b>Team</b> → invite your office staff + field workers. They get an email; it takes them 30 seconds to set a password.</li>
          <li><b>Clients</b> → add the regulars. Even one client is enough to start invoicing.</li>
          <li><b>Jobs</b> → create your first job. Timesheets, SSSPs, hazards, invoices — they all hang off a job.</li>
          <li><b>Dispatch</b> → drag workers onto jobs for tomorrow. Their phones light up with where to be.</li>
        </Steps>
        <Tip>The Overview screen has a <b>setup checklist</b> that follows the same order. Tick each step off as you go — it disappears once you're done.</Tip>
      </>
    ),
  },
  {
    id: "pwa-install",
    category: "start",
    title: "Get Fieldlink on your phone (PWA)",
    blurb: "Add Fieldlink to your home screen — works offline, looks like a real app.",
    tags: ["pwa", "iphone", "android", "install"],
    body: (
      <>
        <P>Fieldlink is a Progressive Web App — no App Store, no downloads, no updates to install. Workers add it to their home screen once and it behaves like a native app.</P>
        <P><b>iPhone</b></P>
        <Steps>
          <li>Open <K>fieldlink.co.nz</K> in <b>Safari</b> (not Chrome — Apple only allows install from Safari).</li>
          <li>Tap the Share button (square with the up-arrow).</li>
          <li>Scroll down → tap <b>Add to Home Screen</b>.</li>
          <li>Done. The orange "F" icon lives next to their other apps.</li>
        </Steps>
        <P><b>Android</b></P>
        <Steps>
          <li>Open <K>fieldlink.co.nz</K> in <b>Chrome</b>.</li>
          <li>Tap the three-dot menu → <b>Add to Home screen</b>.</li>
          <li>Confirm. Done.</li>
        </Steps>
        <Note>Sites that aren't installed don't get push notifications on iPhone. Installing is the only way the lads will get a ping when you @-mention them or change their dispatch.</Note>
      </>
    ),
  },
  {
    id: "logo",
    category: "start",
    title: "Upload your company logo",
    blurb: "Replaces the Fieldlink wordmark on customer-facing PDFs.",
    tags: ["branding", "logo", "pdf"],
    body: (
      <>
        <P>Once your logo is uploaded, every customer-facing PDF (quotes, invoices, SSSPs, pay slips) carries <i>your</i> brand instead of Fieldlink's. We keep a small "Generated by fieldlink.co.nz" line in the footer — that's it.</P>
        <Steps>
          <li>Sidebar → <Link to="/app/branding" className="text-[#FF5F15] font-bold underline">Branding</Link>.</li>
          <li>Click <b>Upload logo</b> and pick a PNG, JPG or SVG (under 2MB).</li>
          <li>That's it — it's live on every PDF from your next download.</li>
        </Steps>
        <Tip>Use a logo with a transparent background if you can. SVG looks crispest at any size.</Tip>
      </>
    ),
  },

  /* ---- TEAM & ROLES ---- */
  {
    id: "roles",
    category: "team",
    title: "Roles explained — admin, office, field",
    blurb: "Who can see and do what.",
    tags: ["roles", "permissions"],
    body: (
      <>
        <P>Fieldlink has three roles. Pick the right one when you invite someone — you can always change it later.</P>
        <ul className="text-sm space-y-2 my-3 list-disc pl-5">
          <li><b>Admin</b> — full control. Can edit billing details, change roles, delete jobs, hire/fire. Usually you (the owner) + your trusted office manager.</li>
          <li><b>Office</b> — runs day-to-day. Can approve timesheets/leave, dispatch jobs, raise quotes/invoices, run payroll. Can't change billing details or platform settings.</li>
          <li><b>Field</b> — the crew. Sees only their own dispatched jobs, their own timesheets, their assigned vehicle. Can submit timesheets, hazards, toolbox talks, pre-starts.</li>
        </ul>
        <Note>Field workers can't see other workers' pay, leave or timesheets. They <i>can</i> see who else is dispatched on their job (so they know who to ring if someone doesn't show up).</Note>
      </>
    ),
  },
  {
    id: "invite-team",
    category: "team",
    title: "Invite your team",
    blurb: "Get the lads (and your office staff) onto Fieldlink.",
    tags: ["invite", "onboarding", "team"],
    body: (
      <>
        <Steps>
          <li>Sidebar → <Link to="/app/team" className="text-[#FF5F15] font-bold underline">Team</Link> → click <b>Invite</b>.</li>
          <li>Enter their email + name → pick a role (Admin / Office / Field) → Send.</li>
          <li>They get an email with a <b>set your password</b> link (valid 7 days). Once they set it, they can log in.</li>
        </Steps>
        <P>Don't have the lads' emails? Two options:</P>
        <ul className="text-sm space-y-1.5 list-disc pl-5">
          <li>Get them to sign up at <K>fieldlink.co.nz</K> themselves using their work email — you'll then see them in the Team tab to assign their role.</li>
          <li>Or use placeholder emails (e.g., <K>ben.field@yourcompany.co.nz</K>) — they can change to a real email later from their Profile screen.</li>
        </ul>
        <Tip>Tell your crew to install the PWA <i>before</i> their first day on Fieldlink. Saves a 5-minute "how do I log in" moment on-site.</Tip>
      </>
    ),
  },
  {
    id: "hourly-rate",
    category: "team",
    title: "Set hourly rates for payroll",
    blurb: "Required for the weekly pay run + gross-pay calculations.",
    tags: ["payroll", "rates"],
    body: (
      <>
        <Steps>
          <li>Sidebar → <Link to="/app/team" className="text-[#FF5F15] font-bold underline">Team</Link>.</li>
          <li>Click a worker's row → set their <b>Hourly rate (NZD)</b> → Save.</li>
        </Steps>
        <P>The rate is private — only admins/office see it. Workers see only their own pay slips after each pay run.</P>
      </>
    ),
  },
  {
    id: "competencies",
    category: "team",
    title: "Worker competencies & WoF/cert tracking",
    blurb: "Skills matrix + certificate expiries for WorkSafe audits.",
    tags: ["competencies", "certs", "audits"],
    body: (
      <>
        <P>The <Link to="/app/competencies" className="text-[#FF5F15] font-bold underline">Competencies</Link> tab is a two-in-one register:</P>
        <ul className="text-sm space-y-1.5 list-disc pl-5">
          <li><b>Skills matrix</b> — rate every worker 0–4 on every hand-tool, plant operator skill and site skill. Auditors love this.</li>
          <li><b>Certificates</b> — upload Site Safe, First Aid, Class 4, Confined Space, etc. with optional expiry dates.</li>
        </ul>
        <Tip>Export the full matrix as CSV from the toolbar → great for tender submissions.</Tip>
      </>
    ),
  },

  /* ---- TIMESHEETS & PAYROLL ---- */
  {
    id: "timesheet-flow",
    category: "timesheets",
    title: "How timesheets flow from field to pay run",
    blurb: "The whole pipeline in 5 boxes.",
    tags: ["timesheets", "payroll", "flow"],
    body: (
      <>
        <P>Hours move through five states:</P>
        <Steps>
          <li><b>Draft</b> — the worker started filling it in but hasn't hit submit. Saved offline.</li>
          <li><b>Pending</b> — submitted, awaiting your approval. Office sees these in the Timesheets tab.</li>
          <li><b>Approved</b> — you OK'd it. Ready for the pay run.</li>
          <li><b>Paid</b> — included in a processed pay run. Locked, can't be edited.</li>
          <li><b>Rejected</b> — you bounced it back to the worker with a reason. They edit + resubmit.</li>
        </Steps>
        <P>The Payroll page only shows <b>Approved + unpaid</b> hours. So if a worker's hours are missing from the pay run, check that they're approved.</P>
      </>
    ),
  },
  {
    id: "missing-timesheets",
    category: "timesheets",
    title: "Chasing missing timesheets",
    blurb: "Who hasn't filled in today/this week.",
    tags: ["timesheets", "missing", "chasing"],
    body: (
      <>
        <P>At the top of the <Link to="/app/timesheets" className="text-[#FF5F15] font-bold underline">Timesheets</Link> page there's a collapsible <b>Missing timesheets</b> panel showing every worker without an entry today, plus the Mon–Fri backlog.</P>
        <P>Tap any name → it pre-fills a chat ping to that worker. They get a push notification + a row in their Inbox.</P>
      </>
    ),
  },
  {
    id: "pay-run",
    category: "timesheets",
    title: "Running a weekly pay run",
    blurb: "Approve hours → process → email payslips.",
    tags: ["payroll", "pay-run"],
    body: (
      <>
        <Steps>
          <li>Approve all pending timesheets for the week (Timesheets tab).</li>
          <li>Sidebar → <Link to="/app/payroll" className="text-[#FF5F15] font-bold underline">Payroll</Link>. The page defaults to the most recently completed pay week.</li>
          <li>Review each worker's hours × hourly rate. Tweak the "Week ending" date if needed (the change saves org-wide).</li>
          <li>Click <b>Process pay run</b> → optionally add a note → confirm.</li>
          <li>Pay slips are auto-generated as PDFs and emailed to every worker.</li>
        </Steps>
        <Note>Processing a pay run marks those timesheets as <K>paid</K> and locks them. You can't edit them after — only an admin can void a pay run.</Note>
      </>
    ),
  },
  {
    id: "pay-week-day",
    category: "timesheets",
    title: "Change your pay-week ending day",
    blurb: "One setting — everyone's tiles align.",
    tags: ["payroll", "pay-week", "config"],
    body: (
      <>
        <P>NZ civil pay weeks usually end on a Saturday. If yours doesn't (e.g., Friday or Sunday), change it once:</P>
        <Steps>
          <li>Sidebar → <Link to="/app/payroll" className="text-[#FF5F15] font-bold underline">Payroll</Link>.</li>
          <li>In the <b>Week ending</b> date input at the top, pick any date that matches your preferred end day (e.g., this Friday).</li>
          <li>That's it — the change is saved on your organisation. Every field worker's "Hours this week" tile updates automatically, and future pay runs default to that rhythm.</li>
        </Steps>
      </>
    ),
  },

  /* ---- JOBS & DISPATCH ---- */
  {
    id: "create-job",
    category: "jobs",
    title: "Create your first job",
    blurb: "The thing everything else hangs off.",
    tags: ["jobs", "create"],
    body: (
      <>
        <Steps>
          <li>Sidebar → <Link to="/app/jobs" className="text-[#FF5F15] font-bold underline">Jobs</Link> → <b>New job</b>.</li>
          <li>Pick a client (or create one inline), give the job a title, scope, site address, start/end dates.</li>
          <li>Save. The job now exists for timesheet logging, SSSPs, hazards and invoicing.</li>
        </Steps>
        <Tip>Upload plans / drawings to the job once and every field worker dispatched to it can pinch-zoom them from their phone.</Tip>
      </>
    ),
  },
  {
    id: "dispatch",
    category: "jobs",
    title: "Dispatch crews for tomorrow",
    blurb: "Drag-and-drop workers onto jobs by date.",
    tags: ["dispatch", "schedule"],
    body: (
      <>
        <P>Dispatch is the source-of-truth for "who's where tomorrow". Don't rely on permanent job assignments — those just mark who's <i>running</i> a project, not who's <i>on it</i> on any given day.</P>
        <Steps>
          <li>Sidebar → <Link to="/app/crew-planner?view=day" className="text-[#FF5F15] font-bold underline">Crew Planner → Day Board</Link>.</li>
          <li>Pick the date you're planning for.</li>
          <li>Drag a worker's name onto a job tile. Optionally drag a vehicle onto the same tile.</li>
          <li>Add a per-worker <b>scope of works</b> note if their tasks differ from the rest of the crew.</li>
          <li>Done. Workers see their tomorrow tile light up the moment you drop them — push notification fires too if they've installed the PWA.</li>
        </Steps>
        <Note>A worker can be dispatched to multiple jobs in one day (split shift). Or marked as <b>day off</b> by dragging them onto the day-off lane.</Note>
      </>
    ),
  },
  {
    id: "variations",
    category: "jobs",
    title: "Job variations — extra work, extra pay",
    blurb: "Capture client-approved scope creep before you forget.",
    tags: ["variations", "scope-change"],
    body: (
      <>
        <P>A <b>variation</b> is a client-approved change to the original scope. Get it signed off the same day or you'll forget by month-end and eat the cost.</P>
        <Steps>
          <li>Sidebar → <Link to="/app/variations" className="text-[#FF5F15] font-bold underline">Variations</Link> → <b>New variation</b>.</li>
          <li>Pick the job, describe the change, set the value (NZD), upload photos if useful.</li>
          <li>Email it to the client for signature. Once signed, mark it <b>Approved</b>.</li>
          <li>The variation now shows on the next invoice for that job automatically.</li>
        </Steps>
      </>
    ),
  },

  /* ---- SAFETY ---- */
  {
    id: "sssp",
    category: "safety",
    title: "Site-Specific Safety Plans (SSSPs)",
    blurb: "WorkSafe-compliant SSSP in 5 minutes.",
    tags: ["sssp", "safety", "worksafe"],
    body: (
      <>
        <P>Every site needs a Site-Specific Safety Plan. Fieldlink walks you through a 10-section template that covers hazard ID, controls, PPE, emergency plan, induction record.</P>
        <Steps>
          <li>Sidebar → <Link to="/app/sssps" className="text-[#FF5F15] font-bold underline">SSSPs</Link> → <b>New SSSP</b>.</li>
          <li>Link it to a job. Fill in each section (most are tick-boxes + a few sentences).</li>
          <li>Lock it before site start. Workers sign the induction page on their phones.</li>
          <li>PDF the whole thing for the client / principal contractor.</li>
        </Steps>
        <Tip>SSSPs are linked to the ISO week of the job. Each new week gets a fresh SSSP (auditors expect this).</Tip>
      </>
    ),
  },
  {
    id: "hazards",
    category: "safety",
    title: "Reporting hazards & incidents",
    blurb: "Field workers can log a hazard in 30 seconds.",
    tags: ["hazards", "incidents", "safety"],
    body: (
      <>
        <P>Field workers tap <b>Safety</b> in their bottom nav → pick <b>Hazard</b> or <b>Incident</b> → snap a photo, describe what happened, hit submit. The office sees a red badge on the Safety tab within seconds.</P>
        <P>Every incident is auto-numbered and timestamped — meets WorkSafe record-keeping requirements.</P>
      </>
    ),
  },
  {
    id: "toolbox-talks",
    category: "safety",
    title: "Toolbox talks",
    blurb: "Weekly talks signed by every attendee on their phone.",
    tags: ["toolbox", "talks", "safety"],
    body: (
      <>
        <P>Fieldlink uses the <b>SSSP Form 6</b> template. Field workers can run the talk themselves — they tap <b>Toolbox</b> in their bottom nav, pick a job, fill in the 8 sections, and every attendee signs on the same phone.</P>
        <Tip>Use this on Monday mornings on-site. Faster than paper, you've got a digital signature register, and the PDF is in your audit trail forever.</Tip>
      </>
    ),
  },

  /* ---- FLEET ---- */
  {
    id: "fleet-basics",
    category: "fleet",
    title: "Add your fleet & track WoF / Rego",
    blurb: "Keep on top of compliance dates.",
    tags: ["fleet", "wof", "rego"],
    body: (
      <>
        <Steps>
          <li>Sidebar → <Link to="/app/fleet" className="text-[#FF5F15] font-bold underline">Fleet</Link> → <b>Add vehicle</b>.</li>
          <li>Rego, make, model, photo. Set WoF + Rego expiry dates.</li>
          <li>Optionally assign a permanent driver (overrides per-job dispatch).</li>
        </Steps>
        <P>The Overview shows a red strip on the dashboard when anything is expiring within 30 days.</P>
      </>
    ),
  },
  {
    id: "cartrack",
    category: "fleet",
    title: "Connect Cartrack for live GPS + RUC",
    blurb: "Live map + remaining RUC kilometres per truck.",
    tags: ["cartrack", "gps", "ruc"],
    body: (
      <>
        <P>If you have a Cartrack account, paste your API credentials into Fieldlink and we'll show every vehicle's live location, current odometer and remaining RUC kilometres on the <Link to="/app/live-tracking" className="text-[#FF5F15] font-bold underline">Live tracking</Link> page.</P>
        <Steps>
          <li>Get your Cartrack API username + password from your Cartrack account manager.</li>
          <li>In Fieldlink → <Link to="/app/live-tracking" className="text-[#FF5F15] font-bold underline">Live tracking</Link> → <b>Connect Cartrack</b>.</li>
          <li>Paste credentials. They're encrypted at rest — the browser never sees them again.</li>
          <li>Match Fieldlink vehicles to Cartrack vehicles by rego.</li>
        </Steps>
        <Note>RUC auto-buying isn't supported (we won't store credit cards or scrape NZTA — PCI risk + ToS breach). Use the deep-link button to NZTA for one-tap RUC purchases.</Note>
      </>
    ),
  },
  {
    id: "pre-starts",
    category: "fleet",
    title: "Daily vehicle pre-starts",
    blurb: "Mandatory checks before the truck leaves the yard.",
    tags: ["prestarts", "fleet", "safety"],
    body: (
      <>
        <P>Drivers tap <b>Pre-start</b> on their phone before they leave the yard. The form is 6 sections × tick-box (tyres, lights, fluids, load, comms, danger zone). Photos optional. Takes 60 seconds.</P>
        <P>Any "unsafe" tick → flagged red in the Overview <b>Unsafe pre-starts</b> tile so the office can pull the truck off the road before the driver leaves.</P>
      </>
    ),
  },

  /* ---- BILLING ---- */
  {
    id: "first-quote",
    category: "billing",
    title: "Send your first quote",
    blurb: "From job → quote → email → PDF in under 2 minutes.",
    tags: ["quotes", "billing"],
    body: (
      <>
        <Steps>
          <li>Make sure you've added billing details (GST + bank) in <Link to="/app/billing" className="text-[#FF5F15] font-bold underline">Billing</Link>.</li>
          <li>Sidebar → <Link to="/app/quotes" className="text-[#FF5F15] font-bold underline">Quotes</Link> → <b>New quote</b>.</li>
          <li>Pick the client + job. Add line items (description + qty + unit price). GST is auto-calc'd at 15%.</li>
          <li>Hit <b>Email</b>. The customer gets a branded PDF and you get a copy.</li>
        </Steps>
        <Tip>Save common line items in the <Link to="/app/rate-library" className="text-[#FF5F15] font-bold underline">Rate library</Link>. Next quote, just pick them off the menu.</Tip>
      </>
    ),
  },
  {
    id: "invoice-customer",
    category: "billing",
    title: "Invoice a customer",
    blurb: "Approved variations + signed-off hours roll into one PDF.",
    tags: ["invoices", "billing"],
    body: (
      <>
        <P>Invoices for the customer (not supplier bills) live under <Link to="/app/customer-invoices" className="text-[#FF5F15] font-bold underline">Customer invoices</Link>.</P>
        <Steps>
          <li>New invoice → pick a job → choose a billing mode (fixed price, time + materials, or progress).</li>
          <li>Fieldlink pre-fills line items from the job's approved variations + timesheet hours × hourly rate.</li>
          <li>Tweak as needed. Add a custom message. Email to client → done.</li>
        </Steps>
        <Note>The default payment term is 14 days. Change it in <Link to="/app/billing" className="text-[#FF5F15] font-bold underline">Billing</Link> → <b>Payment terms (days)</b>.</Note>
      </>
    ),
  },

  /* ---- ADMIN ---- */
  {
    id: "subscription",
    category: "admin",
    title: "Your Fieldlink subscription",
    blurb: "Plans, seats, upgrading.",
    tags: ["subscription", "plan", "billing"],
    body: (
      <>
        <P>Fieldlink charges per active user per month. There are three plans:</P>
        <ul className="text-sm space-y-1.5 list-disc pl-5">
          <li><b>Crew</b> — up to 12 staff, $29/mo</li>
          <li><b>Site</b> — up to 50 staff, $49/mo</li>
          <li><b>Enterprise</b> — 50+ staff, $99/mo</li>
        </ul>
        <P>Trials are 14 days, no card required. After that you'll get a tax invoice each month — pay by bank transfer or credit card.</P>
      </>
    ),
  },
  {
    id: "data-export",
    category: "admin",
    title: "Exporting your data",
    blurb: "CSVs, PDFs and full backups.",
    tags: ["export", "data", "backup"],
    body: (
      <>
        <P>Almost every page has an <b>Export</b> button in the top-right that downloads either a CSV or a PDF of what you're looking at — pay runs, timesheet weeks, competency matrices, customer invoices.</P>
        <P>Need a full backup? Email <a href="mailto:support@fieldlink.co.nz" className="text-[#FF5F15] font-bold underline">support@fieldlink.co.nz</a> and we'll send you a JSON dump of every record under your org_id.</P>
      </>
    ),
  },
  {
    id: "still-stuck",
    category: "admin",
    title: "Still stuck? Get a human",
    blurb: "How to reach us when the help guide doesn't cut it.",
    tags: ["support", "contact", "help"],
    body: (
      <>
        <P><b>The fastest ways to get a real person:</b></P>
        <ul className="text-sm space-y-1.5 list-disc pl-5">
          <li>Email <a href="mailto:support@fieldlink.co.nz" className="text-[#FF5F15] font-bold underline">support@fieldlink.co.nz</a> — usually replied to within 2 working hours during NZ business hours.</li>
          <li>Sidebar → <Link to="/app/support" className="text-[#FF5F15] font-bold underline">Support</Link> → raise a ticket from inside the app. We see it instantly.</li>
        </ul>
        <Tip>If something is broken, paste a screenshot. Saves 3 emails back-and-forth working out what you're looking at.</Tip>
      </>
    ),
  },
];
